import React, { useState, useEffect } from 'react';
import { auth, supabase } from '@/api';
import { validateNewPassword, MIN_PASSWORD_LENGTH } from '@/lib/passwordReset';
import { KeyRound, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

// Délai laissé au client Supabase pour consommer le fragment de l'URL et ouvrir
// la session de récupération. Au-delà, le lien est considéré comme périmé.
const SESSION_TIMEOUT_MS = 8000;
// Temps d'affichage du message de succès avant de repartir sur le dashboard.
const REDIRECT_DELAY_MS = 1500;

const CARD_INPUT_CLASS =
  'w-full h-11 px-3 rounded-xl bg-secondary/50 border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary';

export default function ResetPassword() {
  // 'checking' : on attend que Supabase ouvre la session du lien.
  // 'ready'    : session ouverte, le formulaire peut être soumis.
  // 'missing'  : aucun jeton valide — lien déjà utilisé ou expiré.
  const [sessionState, setSessionState] = useState('checking');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let annule = false;

    const marquerPret = () => {
      if (!annule) setSessionState('ready');
    };

    // La session peut déjà exister (utilisateur connecté qui change son mot de
    // passe) ou arriver quelques centaines de ms plus tard, quand le client a
    // fini de lire le fragment de l'URL.
    supabase.auth.getSession().then(({ data }) => {
      if (data?.session) marquerPret();
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) marquerPret();
    });

    const timer = setTimeout(() => {
      if (!annule) setSessionState((prev) => (prev === 'ready' ? prev : 'missing'));
    }, SESSION_TIMEOUT_MS);

    return () => {
      annule = true;
      clearTimeout(timer);
      sub?.subscription?.unsubscribe();
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const verdict = validateNewPassword(password, confirmation);
    if (!verdict.ok) {
      setError(verdict.error);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await auth.updatePassword(password);
      setDone(true);
      // Rechargement complet : il purge le fragment de récupération de l'URL,
      // sans quoi l'application resterait bloquée sur cette page.
      setTimeout(() => window.location.assign('/'), REDIRECT_DELAY_MS);
    } catch (err) {
      setError(err.message || 'Impossible de modifier le mot de passe');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 safe-area-pt safe-area-pb">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
            style={{
              background: 'hsl(var(--primary))',

            }}>
            <span className="text-white font-bold text-lg" style={{ letterSpacing: '-0.03em' }}>WF</span>
          </div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">Nouveau mot de passe</h1>
          <p className="text-sm text-muted-foreground mt-1">Choisissez le mot de passe de votre compte</p>
        </div>

        {sessionState === 'checking' && (
          <div className="kpi-card-modern p-6 flex flex-col items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Vérification du lien…</p>
          </div>
        )}

        {sessionState === 'missing' && (
          <div className="kpi-card-modern p-6 space-y-4">
            <div className="flex items-start gap-2 p-3 rounded-xl bg-rose/10 border border-rose/20 text-sm text-rose">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                Ce lien de réinitialisation a expiré ou a déjà été utilisé.
                Demandez-en un nouveau depuis la page de connexion.
              </span>
            </div>
            <button
              type="button"
              onClick={() => window.location.assign('/')}
              className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors"
            >
              Retour à la connexion
            </button>
          </div>
        )}

        {sessionState === 'ready' && (
          <form onSubmit={submit} className="kpi-card-modern space-y-4 p-6">
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-foreground">
                Nouveau mot de passe
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={`${MIN_PASSWORD_LENGTH} caractères minimum`}
                className={CARD_INPUT_CLASS}
                disabled={loading || done}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="confirmation" className="text-sm font-medium text-foreground">
                Confirmation
              </label>
              <input
                id="confirmation"
                type="password"
                autoComplete="new-password"
                required
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                placeholder="••••••••"
                className={CARD_INPUT_CLASS}
                disabled={loading || done}
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-rose/10 border border-rose/20 text-sm text-rose">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {done && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-emerald/10 border border-emerald/20 text-sm text-emerald">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Mot de passe enregistré. Ouverture du dashboard…</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || done || !password || !confirmation}
              className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-medium text-sm flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Enregistrement…
                </>
              ) : (
                <>
                  <KeyRound className="w-4 h-4" />
                  Enregistrer
                </>
              )}
            </button>
          </form>
        )}

        <p className="text-center text-xs text-muted-foreground mt-6">
          © Webforce Benelux · CRM Webforce Group
        </p>
      </div>
    </div>
  );
}
